import {AsyncPage, Empty} from 'Core/models/Async';
import {EmployerEnrollment} from 'Enrollment/models/EmployerEnrollment';
import {EmployerEnrollmentDetails} from 'Enrollment/models/EmployerEnrollmentDetails';
import {createAction} from 'Shared/utilities/actionCreator';
import {Page} from 'Core/models/Page';
import {pipe} from 'fp-ts/lib/pipeable';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {constant} from 'fp-ts/lib/function';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import * as O from 'fp-ts/lib/Option';
import {InequalityRangeFilter} from 'Core/models/InequalityRange';

export enum EmployerEnrollmentActionType {
  GET_EMPLOYER_ENROLLMENTS = 'enrollment/GET_EMPLOYER_ENROLLMENTS',
  CHANGE_EMPLOYER_ENROLLMENTS = 'enrollment/CHANGE_EMPLOYER_ENROLLMENTS',
  CHANGE_EMPLOYER_ENROLLMENTS_FILTER = 'enrollment/CHANGE_EMPLOYER_ENROLLMENTS_FILTER',
  GET_EMPLOYER_ENROLLMENT_DETAILS = 'enrollment/GET_EMPLOYER_ENROLLMENT_DETAILS',
  CHANGE_EMPLOYER_ENROLLMENT_DETAILS = 'enrollment/CHANGE_EMPLOYER_ENROLLMENT_DETAILS',
  CHANGE_EMPLOYER_ENROLLMENT_DETAILS_FILTER = 'enrollment/CHANGE_EMPLOYER_ENROLLMENT_DETAILS_FILTER',
}

export const DefaultEnrollmentFilters: EmployerEnrollmentFilters = {
  pageSize: 0,
  accountNumber: '',
  numberOfSuccessfulRecords: O.none,
  numberOfInProgressRecords: O.none,
  numberOfOutstandingDiscrepancies: O.none,
  numberOfDiscrepancies: O.none,
  ageOfOldestOutstandingDiscrepancyInDays: O.none,
  agentNumbers: '',
  isNewYork: false,
  encryptedIdentifier: '',
  agentNumber: ''
};

export type EmployerEnrollmentFilters = {
  pageSize: number;
  accountNumber: string;
  numberOfSuccessfulRecords: InequalityRangeFilter;
  numberOfInProgressRecords: InequalityRangeFilter;
  numberOfOutstandingDiscrepancies: InequalityRangeFilter;
  numberOfDiscrepancies: InequalityRangeFilter;
  ageOfOldestOutstandingDiscrepancyInDays: InequalityRangeFilter;
  agentNumbers: string;
  isNewYork: boolean;
  encryptedIdentifier: string;
  agentNumber: string;
};

export const DefaultEnrollmentDetailsFilters: EmployerEnrollmentDetailsFilters = {
  accountNumber: '',
  discrepantStatusId: '',
  requestType: '',
  ssn: '',
  firstName: '',
  lastName: '',
  product: '',
  caseNumber: '',
  fileName: '',
  agentNumbers: '',
  isNewYork: false,
  encryptedIdentifier: ''
};

export type EmployerEnrollmentDetailsFilters = {
  accountNumber: string;
  discrepantStatusId: string;
  requestType: string;
  ssn: string;
  firstName: string;
  lastName: string;
  product: string;
  caseNumber: string;
  fileName: string;
  agentNumbers?: string;
  isNewYork?: boolean;
  encryptedIdentifier?: string;
};

export const getEmployerEnrollments = (pageOption?: Page<EmployerEnrollment>, filter?: EmployerEnrollmentFilters) => {
  
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );
  return createAction(EmployerEnrollmentActionType.GET_EMPLOYER_ENROLLMENTS, payload);
};

export const changeEmployerEnrollments = (employerEnrollmentReports: AsyncPage<Page<EmployerEnrollment>>) =>
  createAction(EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENTS, employerEnrollmentReports);

export const changeEnrollmentsFilters = (filters: EmployerEnrollmentFilters) =>
  createAction(EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENTS_FILTER, {filters});

export const getEmployerEnrollmentDetails = (
  pageOption?: Page<EmployerEnrollmentDetails>,
  filter?: EmployerEnrollmentDetailsFilters
) => {
  const payload = pipe(
    fromNullable(pageOption),
    foldOption(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );
  return createAction(EmployerEnrollmentActionType.GET_EMPLOYER_ENROLLMENT_DETAILS, payload);
};

export const changeEmployerEnrollmentDetails = (
  employerEnrollmentDetails: AsyncPage<Page<EmployerEnrollmentDetails>>
) => createAction(EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENT_DETAILS, employerEnrollmentDetails);

export const changeEnrollmentDetailsFilters = (filters: EmployerEnrollmentDetailsFilters) =>
  createAction(EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENT_DETAILS_FILTER, {filters});

type EmployerEnrollmentState = {
  employerEnrollmentPage: AsyncPage<Page<EmployerEnrollment>>;
  filter: EmployerEnrollmentFilters;
  employerEnrollmentDetailPage: AsyncPage<Page<EmployerEnrollmentDetails>>;
  enrollmentFileDetailsFilter: EmployerEnrollmentDetailsFilters;
};

export const DEFAULT_STATE: EmployerEnrollmentState = {
  employerEnrollmentPage: new Empty(),
  filter: DefaultEnrollmentFilters,
  employerEnrollmentDetailPage: new Empty(),
  enrollmentFileDetailsFilter: DefaultEnrollmentDetailsFilters,
};

type EmployerEnrollmentAction =
  | ReturnType<typeof getEmployerEnrollments>
  | ReturnType<typeof changeEmployerEnrollments>
  | ReturnType<typeof changeEnrollmentsFilters>
  | ReturnType<typeof getEmployerEnrollmentDetails>
  | ReturnType<typeof changeEmployerEnrollmentDetails>
  | ReturnType<typeof changeEnrollmentDetailsFilters>;

export default (
  state: EmployerEnrollmentState = DEFAULT_STATE,
  action: EmployerEnrollmentAction
): EmployerEnrollmentState => {
  switch (action.type) {
    case EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENTS:
      return {
        ...state,
        employerEnrollmentPage: action.payload,
      };
    case EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENTS_FILTER:
      return {
        ...state,
        filter: action.payload.filters,
      };
    case EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENT_DETAILS:
      return {
        ...state,
        employerEnrollmentDetailPage: action.payload,
      };
    case EmployerEnrollmentActionType.CHANGE_EMPLOYER_ENROLLMENT_DETAILS_FILTER:
      return {
        ...state,
        enrollmentFileDetailsFilter: action.payload.filters,
      };
    default:
      return state;
  }
};
